<script setup lang="ts">
import { computed } from 'vue';
import { useMissionCard } from '~/composables/useMissionCard';

const ALBUM_CUSTOM_SECTION_ID = 1541;

const props = defineProps<{
  mission: TMissionOrBadge;
  isFeatured?: boolean;
}>();

const emit = defineEmits<{
  missionOptedIn: [missionId: number];
}>();

const {
  mission,
  isFeatured,
  timer,
  missionProgress,
  missionProgressText,
  rewardData,
  buttonIcons,
  buttonState,
} = useMissionCard(props, (missionId) => emit('missionOptedIn', missionId));

const isAlbumMission = computed(
  () => props.mission?.custom_section_id === ALBUM_CUSTOM_SECTION_ID,
);

const unlockOverlayText = computed(
  () =>
    props.mission?.unlock_mission_description?.trim() || 'Missão indisponível',
);
</script>

<template>
  <div :class="$style.cardWrapper">
    <div
      :class="[$style.missionCard, isFeatured ? $style.featuredMission : '']"
      draggable="false"
      @dragstart.prevent
    >
      <!-- Ribbon no topo centralizado -->
      <div
        v-if="mission.ribbon"
        :class="$style.ribbon"
      >
        <span :class="$style.ribbonText">{{ mission.ribbon }}</span>
      </div>

      <!-- Main Content -->
      <div :class="$style.cardContent">
        <!-- Header: Image + Info -->
        <div :class="$style.headerSection">
          <SmarticoApostatudoSmarticoImage
            :alt="mission.name"
            :class="$style.missionImage"
            :src="mission.image"
          />
          <div :class="$style.missionInfo">
            <p :class="$style.missionName">
              {{ mission.name }}
            </p>
            <p :class="$style.missionDescription">
              {{ mission.description }}
            </p>
          </div>
        </div>

        <!-- Reward Section -->
        <div
          v-if="mission.reward"
          :class="$style.rewardSection"
        >
          <!-- Duration Row -->
          <div :class="$style.durationRow">
            <span :class="$style.durationLabel">DURAÇÃO</span>
            <div :class="$style.durationValue">
              <NuxtIcon
                :class="$style.clockIcon"
                :filled="true"
                name="mission/duracao"
              />
              <span :class="$style.timerText">
                {{ Number(timer?.days) > 0 ? `${timer.days}d ` : ''
                }}{{ timer.hours }}:{{ timer.minutes }}:{{ timer.seconds }}
              </span>
            </div>
          </div>
          <!-- Reward Row -->
          <div :class="$style.rewardRow">
            <NuxtIcon
              :class="$style.giftIcon"
              :filled="true"
              name="mission/gift"
            />
            <span :class="$style.rewardText">{{ rewardData.reward }}</span>
          </div>
        </div>

        <!-- Progress Section -->
        <div :class="$style.progressSection">
          <div :class="$style.progressContainer">
            <div :class="$style.progressBarWrapper">
              <div :class="$style.progressBar">
                <div
                  :class="$style.progressFill"
                  :style="{ width: `${missionProgress}%` }"
                >
                  <span :class="$style.progressText">{{
                    missionProgressText
                  }}</span>
                </div>
              </div>
            </div>
            <div :class="$style.progressIcons">
              <NuxtIcon
                :class="$style.energyIcon"
                :filled="true"
                name="mission/energy"
              />
              <NuxtIcon
                :class="$style.trophyIcon"
                :filled="true"
                name="mission/trophy"
              />
            </div>
          </div>
        </div>

        <!-- Action Button -->
        <div
          v-if="buttonState"
          :class="$style.actionButton"
        >
          <NuxtIcon
            v-for="(icon, index) in buttonIcons"
            :key="index"
            :class="$style.buttonIcon"
            :name="icon"
          />
          <span :class="$style.buttonText">{{ buttonState.text }}</span>
        </div>
      </div>

      <!-- Overlay com descrição de como desbloquear (somente missões de Figurinhas) -->
      <div
        v-if="isAlbumMission"
        :class="$style.unlockOverlay"
      >
        <NuxtIcon
          :class="$style.unlockOverlayIcon"
          :filled="true"
          name="mission/button/indisponivel"
        />
        <p :class="$style.unlockOverlayText">{{ unlockOverlayText }}</p>
      </div>
    </div>
  </div>
</template>

<style module lang="postcss">
/* === CARD WRAPPER === */
.cardWrapper {
  width: 100%;
  min-width: 0;
  padding-top: 10px;
  /* Isola contexto de composição para evitar vazamento de blur/shadow */
  isolation: isolate;
  contain: layout style paint;
}

/* Dentro de swipers: largura fixa */
:global(.afiliado-swiper__items) .cardWrapper {
  width: 165px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .cardWrapper {
    padding-top: 14px;
  }

  :global(.afiliado-swiper__items) .cardWrapper {
    width: 260px;
  }
}

.missionCard {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 9px;
  border-radius: 6px;
  overflow: visible;
  user-select: none;
  background: linear-gradient(
    180deg,
    rgba(11, 18, 32, 0.4) 0%,
    rgba(1, 7, 22, 0.4) 100%
  );
  backdrop-filter: blur(6px);
  min-width: 0;
}

@media (min-width: 640px) {
  .missionCard {
    padding: 14px;
    border-radius: 9px;
    backdrop-filter: blur(10px);
  }
}

.missionCard::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  padding: 0.6px;
  background: linear-gradient(180deg, #1d2329 0%, #1d2329 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: 1;
}

@media (min-width: 640px) {
  .missionCard::before {
    padding: 1px;
  }
}

.cardContent {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
}

.cardContent > *:not(.actionButton) {
  opacity: 0.2;
}

@media (min-width: 640px) {
  .cardContent {
    gap: 7px;
  }
}

.headerSection {
  display: flex;
  align-items: stretch;
  gap: 6px;
  width: 100%;
}

@media (min-width: 640px) {
  .headerSection {
    gap: 10px;
  }
}

.missionImage {
  width: 63px;
  height: 63px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .missionImage {
    width: 94px;
    height: 94px;
    border-radius: 6px;
  }
}

.missionInfo {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 3px;
  justify-content: space-around;
  height: 100%;
}

@media (min-width: 640px) {
  .missionInfo {
    gap: 5px;
  }
}

.missionName {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 15px;
  color: #ffffff;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}

@media (min-width: 640px) {
  .missionName {
    font-size: 19px;
    line-height: 22px;
  }
}

.missionDescription {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 12px;
  color: #a1a1aa;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}

@media (min-width: 640px) {
  .missionDescription {
    font-size: 15px;
    line-height: 19px;
  }
}

.termsButton {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 5px 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 1000px;
  width: fit-content;
}

@media (min-width: 640px) {
  .termsButton {
    display: flex;
  }
}

.termsText {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 16px;
  color: rgba(255, 255, 255, 0.5);
}

.termsIcon {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
}

.rewardSection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

@media (min-width: 640px) {
  .rewardSection {
    gap: 5px;
    border-radius: 10px;
  }
}

.durationRow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding-top: 3px;
}

@media (min-width: 640px) {
  .durationRow {
    gap: 10px;
    padding-top: 5px;
  }
}

.durationLabel {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: 6px;
  line-height: 10px;
  color: #a1a1aa;
}

@media (min-width: 640px) {
  .durationLabel {
    font-size: 10px;
    line-height: 16px;
  }
}

.durationValue {
  display: flex;
  align-items: center;
  gap: 3px;
}

@media (min-width: 640px) {
  .durationValue {
    gap: 5px;
  }
}

.clockIcon {
  font-size: 9px;
  color: #a1a1aa;
}

@media (min-width: 640px) {
  .clockIcon {
    font-size: 15px;
  }
}

.timerText {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 9px;
  line-height: 13px;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
}

@media (min-width: 640px) {
  .timerText {
    font-size: 14px;
    line-height: 20px;
  }
}

.rewardRow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 1px 10px;
  background: #0d2452;
  backdrop-filter: blur(6px);
  border-radius: 0 0 6px 6px;
  height: 24px;
}

@media (min-width: 640px) {
  .rewardRow {
    gap: 10px;
    padding: 2px 16px;
    backdrop-filter: blur(10px);
    border-radius: 0 0 10px 10px;
    height: 38px;
  }
}

.giftIcon {
  font-size: 14px;
  color: #00c9ff;
}

@media (min-width: 640px) {
  .giftIcon {
    font-size: 22px;
  }
}

.rewardText {
  font-weight: 700;
  font-size: 10px;
  color: #ffffff;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 640px) {
  .rewardText {
    font-size: 14px;
  }
}

.progressSection {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 5px;
}

.progressContainer {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 3px;
}

@media (min-width: 640px) {
  .progressContainer {
    gap: 5px;
  }
}

.progressBarWrapper {
  display: flex;
  align-items: center;
  width: 100%;
  height: 16px;
}

@media (min-width: 640px) {
  .progressBarWrapper {
    height: 25px;
  }
}

.progressBar {
  flex: 1;
  height: 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 63px;
  position: relative;
  overflow: hidden;
}

@media (min-width: 640px) {
  .progressBar {
    height: 19px;
    border-radius: 100px;
  }
}

.progressFill {
  height: 100%;
  background: #a4cff8;
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 3px;
  min-width: 29px;
}

@media (min-width: 640px) {
  .progressFill {
    padding-right: 5px;
    min-width: 46px;
  }
}

.progressText {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 800;
  font-size: 7px;
  line-height: 6px;
  color: #010612;
}

@media (min-width: 640px) {
  .progressText {
    font-size: 11px;
    line-height: 10px;
  }
}

.progressIcons {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 16px;
  pointer-events: none;
}

@media (min-width: 640px) {
  .progressIcons {
    height: 25px;
  }
}

.energyIcon {
  font-size: 16px;
  margin-left: -4px;
}

@media (min-width: 640px) {
  .energyIcon {
    font-size: 25px;
    margin-left: -6px;
  }
}

.trophyIcon {
  font-size: 16px;
  margin-right: -4px;
}

@media (min-width: 640px) {
  .trophyIcon {
    font-size: 25px;
    margin-right: -6px;
  }
}

.actionButton {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 29px;
  padding: 0 6px;
  border-radius: 4px;
}

@media (min-width: 640px) {
  .actionButton {
    gap: 7px;
    height: 47px;
    padding: 0 10px;
    border-radius: 6px;
  }
}

.buttonIcon {
  font-size: 9px;
  color: #ffffff;
}

@media (min-width: 640px) {
  .buttonIcon {
    font-size: 17px;
  }
}

.buttonText {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: 8px;
  line-height: 8px;
  color: #ffffff;
  text-align: center;
}

@media (min-width: 640px) {
  .buttonText {
    font-size: 12px;
    line-height: 12px;
  }
}

/* Ribbon no topo centralizado */
.ribbon {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 10;
  min-width: 80px;
  width: auto;
  max-width: calc(100% - 16px);
  height: 18px;
  padding: 0 8px;
  background: rgba(29, 35, 41, 0.7);
  backdrop-filter: blur(8px);
  border-radius: 4px;
  border: 1px solid rgba(29, 35, 41, 0.5);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

@media (min-width: 640px) {
  .ribbon {
    height: 22px;
    min-width: 96px;
    padding: 0 10px;
  }
}

.ribbonText {
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

@media (min-width: 640px) {
  .ribbonText {
    font-size: 11px;
  }
}

.featuredMission {
  border: 1px solid rgba(255, 215, 0, 0.3);
}

/* === UNLOCK OVERLAY (somente missões de Figurinhas indisponíveis) === */
.unlockOverlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  border-radius: inherit;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(1px);
  z-index: 5;
  pointer-events: none;
  text-align: center;
}

@media (min-width: 640px) {
  .unlockOverlay {
    gap: 10px;
    padding: 20px;
  }
}

.unlockOverlayIcon {
  font-size: 22px;
  color: #ffffff;
  opacity: 0.85;
}

@media (min-width: 640px) {
  .unlockOverlayIcon {
    font-size: 32px;
  }
}

.unlockOverlayText {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: 10px;
  line-height: 13px;
  color: #ffffff;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  overflow: hidden;
}

@media (min-width: 640px) {
  .unlockOverlayText {
    font-size: 13px;
    line-height: 17px;
  }
}
</style>
